<template>
  <div id="timeline" class="p-room-timeline" ref="scroll">
    <div class="p-room-timeline__inner">
      <Loading v-if="isLoadingMore" variant="inline" />
      <transition-group name="v-fade--in">
        <Message
          v-for="message in messages"
          :key="message.id"
          :firstUnreadMessageId="firstUnreadMessageId"
          :message="message"
          :roomLastDatetime="roomLastDatetime"
        />
      </transition-group>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/Loading';
import Message from '@/components/room/Message';
import PerfectScrollbar from 'perfect-scrollbar';

export default {
  props: {
    firstUnreadMessageId: {
      type: String,
      required: false
    },
    messages: {
      type: Array,
      required: true
    },
    roomLastDatetime: {
      type: Object,
      required: true
    },
    isLoadingMore: {
      type: Boolean,
      required: true
    }
  },
  components: {
    Loading,
    Message
  },
  data() {
    return {
      layoutSyncTimerId: null,
      ps: null,
      startId: null,
      startOldId: null,
      endId: null,
      endOldId: null
    };
  },
  watch: {
    messages: {
      handler() {
        this.startId = this.messages[0].id;
        this.endId = this.messages[this.messages.length - 1].id;
        if (this.ps) {
          this.updateScrollTop();
        }
        this.startOldId = this.startId;
        this.endOldId = this.endId;
      },
      immediate: true
    }
  },
  mounted() {
    this.ps = new PerfectScrollbar(this.$refs.scroll, {
      minScrollbarLength: 20,
      suppressScrollX: true
    });

    // 未読メッセージがある場合は、そのメッセージの位置までスクロールする
    if (this.firstUnreadMessageId) {
      const elMessage = document.getElementById(this.firstUnreadMessageId);
      if (elMessage) {
        this.scrollToMessage(elMessage);
      } else {
        // 未読メッセージのDOMがない場合は、過去のメッセージをさらに読み込む
        // 1回あたりのメッセージ取得上限が20件として、未読メッセージが21件以上ある場合はここを通る
        this.$emit('loadMore');
      }
    } else {
      this.scrollBottom();
    }
    this.addScrollEvent();
    window.addEventListener('resize', this.updateScrollbar);
    this.$root.$on('room-form-layout-change', this.handleFormLayoutChange);
  },
  beforeDestroy() {
    clearTimeout(this.layoutSyncTimerId);
    this.ps.destroy();
    this.ps = null; // to make sure garbages are collected
    this.removeScrollEvent();
    window.removeEventListener('resize', this.updateScrollbar);
    this.$root.$off('room-form-layout-change', this.handleFormLayoutChange);
  },
  methods: {
    addScrollEvent() {
      this.$refs.scroll.addEventListener('ps-y-reach-start', this.reachStart);
    },
    removeScrollEvent() {
      this.$refs.scroll.removeEventListener(
        'ps-y-reach-start',
        this.reachStart
      );
    },
    reachStart() {
      // 過去のメッセージを読み込み終わるまで上スクロールイベントを止める
      this.removeScrollEvent();
      this.$emit('loadMore');
    },
    scrollTo(scrollTop) {
      this.$refs.scroll.scrollTop = scrollTop;
    },
    scrollToMessage(elMessage) {
      if (!elMessage) {
        return;
      }
      const timelineRect = this.$refs.scroll.getBoundingClientRect();
      const messageRect = elMessage.getBoundingClientRect();
      let scrollTop = messageRect.top - timelineRect.top;
      this.scrollTo(scrollTop);
    },
    scrollBottom() {
      this.$refs.scroll.scrollTop = this.$refs.scroll.scrollHeight;
    },
    isNearBottom(offset = 24) {
      if (!this.$refs.scroll) {
        return false;
      }
      const { offsetHeight, scrollHeight, scrollTop } = this.$refs.scroll;
      return scrollHeight - offsetHeight - scrollTop <= offset;
    },
    syncBottomLayout(shouldStickBottom) {
      this.updateScrollbar();
      if (shouldStickBottom) {
        this.scrollBottom();
      }
    },
    handleFormLayoutChange(payload = {}) {
      const shouldStickBottom =
        payload.messageId === this.endId || this.isNearBottom(80);
      clearTimeout(this.layoutSyncTimerId);
      this.$nextTick(() => {
        this.syncBottomLayout(shouldStickBottom);
        window.requestAnimationFrame(() => {
          this.syncBottomLayout(shouldStickBottom);
        });
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            this.syncBottomLayout(shouldStickBottom);
          });
        });
        this.layoutSyncTimerId = setTimeout(() => {
          this.syncBottomLayout(shouldStickBottom);
        }, 120);
      });
    },
    updateScrollbar() {
      this.ps.update();
    },
    updateScrollTop() {
      const offsetHeight = this.$refs.scroll.offsetHeight;
      const scrollHeight = this.$refs.scroll.scrollHeight;
      let scrollTop = this.$refs.scroll.scrollTop;

      // 上までスクロールして過去のメッセージが増えた時
      if (this.startId !== this.startOldId) {
        // DOM更新後に増えた高さの分だけスクロール
        this.$nextTick(() => {
          scrollTop += this.$refs.scroll.scrollHeight - scrollHeight;
          this.scrollTo(scrollTop);
          // 上スクロールイベントを復活させる
          this.addScrollEvent();
        });
      } else if (
        this.endId !== this.endOldId &&
        scrollHeight - offsetHeight === scrollTop
      ) {
        // 下までスクロールしていて新しいメッセージが増えた時
        // DOM更新後に下までスクロール
        this.$nextTick(() => {
          this.scrollBottom();
        });
      }
    }
  }
};
</script>
